export const INPUT_YIELD_BASES = ['as_harvested']

const getValue = (source, camelKey, snakeKey) =>
  source?.[camelKey] ?? source?.[snakeKey] ?? null

const fieldTypeIsNumeric = (field) =>
  /^(?:float|double|decimal|numeric|number|integer|int|real)(?::|\d|\b)/i.test(
    String(field?.type || '')
  )

export const isValidMoisturePercent = (value) =>
  Number.isFinite(value) && value >= 0 && value < 100

export const MOISTURE_UNIFORMITY_TOLERANCE_PERCENT = 0.01
export const MOISTURE_CAP_MINIMUM_RATIO = 0.05
export const MOISTURE_CAP_MINIMUM_COUNT = 20

export const MOISTURE_FIELD_STATES = {
  ORIGINAL_OR_UNEDITED: 'original_or_unedited',
  CAPPED_AT_DETECTED_TARGET: 'capped_at_detected_target',
  UNIFORM_AT_DETECTED_TARGET: 'uniform_at_detected_target',
}

export const MOISTURE_POLICIES = {
  BILATERAL_TO_COMMERCIAL_TARGET: 'bilateral_to_commercial_target',
  EXCESS_ONLY_NO_DRY_BONUS: 'excess_only_no_dry_bonus',
  DRY_ONLY_TO_COMMERCIAL_TARGET: 'dry_only_to_commercial_target',
  ALREADY_CORRECTED_NO_CHANGE: 'already_corrected_no_change',
}

export const getMoistureProcessingPolicies = (inputYieldBasis) => {
  if (inputYieldBasis === 'as_harvested') {
    return {
      economicPolicy: MOISTURE_POLICIES.EXCESS_ONLY_NO_DRY_BONUS,
      layerPolicy: MOISTURE_POLICIES.BILATERAL_TO_COMMERCIAL_TARGET,
    }
  }

  if (inputYieldBasis === 'corrected_excess_only') {
    return {
      economicPolicy: MOISTURE_POLICIES.ALREADY_CORRECTED_NO_CHANGE,
      layerPolicy: MOISTURE_POLICIES.DRY_ONLY_TO_COMMERCIAL_TARGET,
    }
  }

  if (inputYieldBasis === 'normalized_to_target') {
    return {
      economicPolicy: MOISTURE_POLICIES.ALREADY_CORRECTED_NO_CHANGE,
      layerPolicy: MOISTURE_POLICIES.ALREADY_CORRECTED_NO_CHANGE,
    }
  }

  return {
    economicPolicy: null,
    layerPolicy: null,
  }
}

export const getInputCorrectionTargetInference = (inputYieldBasis, field) => {
  if (inputYieldBasis === 'as_harvested') {
    return {
      reason: null,
      state: MOISTURE_FIELD_STATES.ORIGINAL_OR_UNEDITED,
      targetPercent: null,
      valid: true,
    }
  }

  const minValue = getValue(field, 'minValue', 'min_value')
  const maxValue = getValue(field, 'maxValue', 'max_value')

  if (!isValidMoisturePercent(minValue) || !isValidMoisturePercent(maxValue)) {
    return {
      reason: 'missing_stats',
      state: null,
      targetPercent: null,
      valid: false,
    }
  }

  if (inputYieldBasis === 'corrected_excess_only') {
    const numericCount = getValue(field, 'numericCount', 'numeric_count')
    const maxValueCount = getValue(field, 'maxValueCount', 'max_value_count')
    const minimumCapCount = Number.isFinite(numericCount)
      ? Math.max(
          MOISTURE_CAP_MINIMUM_COUNT,
          Math.ceil(numericCount * MOISTURE_CAP_MINIMUM_RATIO)
        )
      : Number.POSITIVE_INFINITY
    const hasDetectedCap =
      Number.isFinite(maxValueCount) && maxValueCount >= minimumCapCount

    return {
      reason: hasDetectedCap ? null : 'moisture_values_unedited',
      state: hasDetectedCap
        ? MOISTURE_FIELD_STATES.CAPPED_AT_DETECTED_TARGET
        : MOISTURE_FIELD_STATES.ORIGINAL_OR_UNEDITED,
      targetPercent: hasDetectedCap ? maxValue : null,
      valid: true,
    }
  }

  if (inputYieldBasis === 'normalized_to_target') {
    if (Math.abs(maxValue - minValue) > MOISTURE_UNIFORMITY_TOLERANCE_PERCENT) {
      return {
        reason: 'moisture_values_unedited',
        state: MOISTURE_FIELD_STATES.ORIGINAL_OR_UNEDITED,
        targetPercent: null,
        valid: true,
      }
    }

    return {
      reason: null,
      state: MOISTURE_FIELD_STATES.UNIFORM_AT_DETECTED_TARGET,
      targetPercent: (minValue + maxValue) / 2,
      valid: true,
    }
  }

  return {
    reason: 'invalid_basis',
    state: null,
    targetPercent: null,
    valid: false,
  }
}

export const getMoistureFieldValidation = (
  inspection,
  fieldName,
  yieldFieldName = ''
) => {
  if (!fieldName) {
    return {valid: false, reason: 'missing'}
  }

  if (fieldName === yieldFieldName) {
    return {valid: false, reason: 'same_as_yield'}
  }

  const field = (inspection?.fields || []).find((candidate) => candidate.name === fieldName)

  if (!field) {
    return {valid: false, reason: 'missing'}
  }

  const featureCount = getValue(inspection, 'featureCount', 'feature_count')
  const numericCount = getValue(field, 'numericCount', 'numeric_count')
  const nullCount = getValue(field, 'nullCount', 'null_count')
  const minValue = getValue(field, 'minValue', 'min_value')
  const maxValue = getValue(field, 'maxValue', 'max_value')

  if (
    !fieldTypeIsNumeric(field) ||
    !Number.isFinite(numericCount) ||
    numericCount < 1
  ) {
    return {valid: false, reason: 'not_numeric'}
  }

  if (
    !Number.isFinite(featureCount) ||
    featureCount < 1 ||
    numericCount !== featureCount ||
    nullCount !== 0
  ) {
    return {valid: false, reason: 'incomplete'}
  }

  if (!isValidMoisturePercent(minValue) || !isValidMoisturePercent(maxValue)) {
    return {valid: false, reason: 'out_of_range'}
  }

  return {valid: true, reason: null}
}

export const getOriginalMoistureAssessment = (
  inspection,
  fieldName,
  yieldFieldName = ''
) => {
  const fieldValidation = getMoistureFieldValidation(
    inspection,
    fieldName,
    yieldFieldName
  )

  if (!fieldValidation.valid) {
    return {
      valid: false,
      reason: fieldValidation.reason,
      state: 'invalid',
      targetPercent: null,
    }
  }

  const field = (inspection?.fields || []).find(
    (candidate) => candidate.name === fieldName
  )
  const minValue = getValue(field, 'minValue', 'min_value')
  const maxValue = getValue(field, 'maxValue', 'max_value')
  const numericCount = getValue(field, 'numericCount', 'numeric_count')
  const maxValueCount = getValue(field, 'maxValueCount', 'max_value_count')

  if (
    Number.isFinite(minValue) &&
    Number.isFinite(maxValue) &&
    Math.abs(maxValue - minValue) <= MOISTURE_UNIFORMITY_TOLERANCE_PERCENT
  ) {
    return {
      valid: false,
      reason: 'uniform_corrected',
      state: 'uniform_corrected',
      targetPercent: (minValue + maxValue) / 2,
    }
  }

  const minimumCorrectedCount = Number.isFinite(numericCount)
    ? Math.max(
        MOISTURE_CAP_MINIMUM_COUNT,
        Math.ceil(numericCount * MOISTURE_CAP_MINIMUM_RATIO)
      )
    : Number.POSITIVE_INFINITY
  if (
    Number.isFinite(maxValueCount) &&
    maxValueCount >= minimumCorrectedCount
  ) {
    return {
      valid: false,
      reason: 'corrected_at_target',
      state: 'corrected_at_target',
      targetPercent: maxValue,
    }
  }

  return {
    valid: true,
    reason: null,
    state: 'original',
    targetPercent: null,
  }
}

export const getSelectedYieldCorrectionAssessment = (
  inspection,
  fieldName,
  moistureFieldName = ''
) => {
  if (!fieldName) {
    return {
      valid: false,
      reason: 'missing',
      relation: null,
      suggestedField: null,
    }
  }

  const relations =
    inspection?.yieldCorrectionRelations ||
    inspection?.yield_correction_relations ||
    []
  const relation = relations.find(
    (candidate) =>
      String(candidate?.correctedField || candidate?.corrected_field || '')
        .trim()
        .toLocaleLowerCase() === String(fieldName).trim().toLocaleLowerCase() &&
      (!moistureFieldName ||
        String(candidate?.moistureField || candidate?.moisture_field || '')
          .trim()
          .toLocaleLowerCase() ===
          String(moistureFieldName).trim().toLocaleLowerCase())
  )

  if (!relation) {
    return {
      valid: true,
      reason: null,
      relation: null,
      suggestedField: null,
    }
  }

  return {
    valid: false,
    reason: 'corrected_yield',
    relation,
    suggestedField: relation.sourceField || relation.source_field || null,
  }
}
